import type { Breadcrumb, Event, EventHint } from '@sentry/browser';
import { DEPLOY_ENV, VERSION } from './utils/config';
import {
  getCompanyUid,
  getIsCypressMode,
  getLanguageId,
} from './utils/globals';

declare global {
  interface Window {
    Sentry: any;
  }
}

export function setupBugTracking() {
  if (
    process.env.NODE_ENV !== 'production' ||
    DEPLOY_ENV !== 'production' ||
    getIsCypressMode()
  )
    return;
  if (!window.Sentry) return;

  window.Sentry.onLoad(() => {
    window.Sentry.init({
      environment: DEPLOY_ENV,
      release: `elevio-kb@${VERSION}`,
      attachStacktrace: true,
      ignoreErrors: ['ResizeObserver loop limit exceeded'],
      beforeSend: (event: Event, hint: EventHint) => {
        // @ts-ignore
        const isInClientsHTML = hint.originalException?.stack?.match(
          /elevioAddScriptInner/
        );
        if (!!isInClientsHTML) return null;

        // Ingore errors from third party scripts.
        // @ts-ignore
        const filename: string | undefined = hint.originalException?.fileName;
        const host = window.location.host;
        if (
          filename &&
          !(
            filename.includes(host) ||
            filename.includes('cdn.elev.io') ||
            filename.includes('cdn.elev-staging.com')
          )
        ) {
          return null;
        }

        // Ignore errors from the Assistant
        if (filename && filename.includes('elevio-main.js')) {
          return null;
        }

        // NOTE: put customer hook here, if they want to be notified about errors.
        return event;
      },
    });

    window.Sentry.configureScope((scope: any) => {
      scope.setTag('company_uid', getCompanyUid());
      scope.setTag('page_locale', getLanguageId());
    });
  });
}

export function reportError(err: Error) {
  if (process.env.NODE_ENV !== 'production' || getIsCypressMode()) {
    console.error(err);
    return;
  }
  if (!window.Sentry) return;
  window.Sentry.captureException(err);
}

export function addBreadCrumb(crumb: Breadcrumb) {
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test')
    return;
  if (!window.Sentry) return;

  window.Sentry.addBreadcrumb({
    ...crumb,
    level: crumb.level || 'log',
  });
}
